import { Link, useLocation } from "react-router-dom";
import {
  LayoutGrid,
  Briefcase,
  Mail,
  BarChart3,
  ShieldCheck,
  Bell,
  CreditCard,
  Settings as SettingsIcon,
  HelpCircle,
  Home,
  Handshake,
  MessagesSquare,
  type LucideIcon,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useGetMyDeals } from "@/hooks/useDeals";
import { useNotificationUnreadCount } from "@/hooks/useNotifications";
import { useChatInbox } from "@/hooks/useDealChat";
import { useChatSocketStatus } from "@/lib/chatSocket";
import { SHARED_PROTECTED_ROUTES } from "@/routes/url";
import { cn } from "@/utils/common.utils";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

interface NavItem {
  label: string;
  icon: LucideIcon;
  to?: string;
  badge?: number;
  /** Explicit test id. Deal entries are labelled by street address, which does
   *  not slugify into anything a test can rely on. */
  testId?: string;
  /** Secondary line under the label — the deal's phase, for deal entries. */
  hint?: string;
}

interface NavGroup {
  /** Small caps heading above the group. Only "Your deals" uses one so far. */
  label?: string;
  items: NavItem[];
}

interface BillingUsage {
  deals_active?: number;
  /** Agent seats. `null` on an uncapped plan. */
  seats_used?: number;
  seats_limit?: number | null;
  /** The plan's one assistant/admin seat, counted apart from agent seats. */
  support_seats_used?: number;
  support_seats_limit?: number;
  plan_name?: string;
}

function useNavGroups(): NavGroup[] {
  const { context } = useAuth();
  const unread = useNotificationUnreadCount();
  const slug = context?.organization?.slug;
  const activeDeals = context?.my_active_deals;
  const isExternal = Boolean(context && !context.membership);
  const participations = context?.deal_participations?.length || 0;
  // Only externals navigate by deal, and only they pay for the request. An
  // internal user's participations are reachable from "Shared with me".
  const { data: myDeals } = useGetMyDeals({ enabled: isExternal && participations > 0 });
  // Same socket as every open conversation, so the badge moves the moment a
  // message lands on any deal — not on the next poll. Agents always have a
  // Chats entry; a participant only once they are in a room.
  const chatStatus = useChatSocketStatus();
  const hasChats = Boolean(context) && (!isExternal || participations > 0);
  const { data: chatInbox } = useChatInbox(chatStatus, hasChats);
  const chatUnread = chatInbox?.total_unread || 0;

  // No Audit for externals. `GET /api/audit` runs `require_org_access` and then
  // an admin-role check, so a client, lender or attorney could only ever reach
  // a 403 through this link — it was a nav item that could not work.
  if (isExternal) {
    // The deals themselves are the navigation. A single "Overview" entry
    // pointing at whichever room happened to be first is what made a second
    // deal unreachable; listing them here means the switcher is on screen the
    // whole time, including while a deal is open.
    const deals = myDeals?.deals ?? [];
    const dealItems: NavItem[] = deals.map((deal) => ({
      label: deal.address,
      hint: deal.phase_label,
      icon: Home,
      to: deal.route,
      testId: `sidebar-deal-${deal.deal_id}`,
    }));
    return [
      {
        label: deals.length > 1 ? "Your deals" : undefined,
        items: [
          // Kept above the list once there is more than one, because the
          // overview carries progress and closing dates a nav row cannot.
          ...(deals.length > 1
            ? [{ label: "All deals", icon: LayoutGrid, to: "/overview" }]
            : []),
          // Falls back to the plain overview entry while the request is in
          // flight, so the sidebar is never momentarily empty.
          ...(dealItems.length
            ? dealItems
            : [{ label: "Overview", icon: LayoutGrid, to: "/overview" }]),
        ],
      },
      {
        items: [
          ...(participations
            ? [{ label: "Chats", icon: MessagesSquare, to: SHARED_PROTECTED_ROUTES.CHATS, badge: chatUnread || undefined }]
            : []),
          { label: "Notifications", icon: Bell, to: "/notifications", badge: unread || undefined },
          { label: "Settings", icon: SettingsIcon, to: "/settings" },
        ],
      },
    ];
  }

  return [
    {
      items: [
        { label: "Dashboard", icon: LayoutGrid, to: "/overview" },
        { label: "Deals", icon: Briefcase, to: "/dashboard", badge: activeDeals },
        { label: "Chats", icon: MessagesSquare, to: SHARED_PROTECTED_ROUTES.CHATS, badge: chatUnread || undefined },
        // Only when there is something behind it. An agent can be invited into
        // another brokerage's deal as a client or co-agent; `GET /deals` is
        // org-scoped and would never show it, so without this entry the deal was
        // reachable only from the original invitation email.
        ...(participations
          ? [{ label: "Shared with me", icon: Handshake, to: "/shared", badge: participations }]
          : []),
      ],
    },
    { items: [{ label: "Invitations", icon: Mail, to: "/invites" }] },
    {
      items: [
        { label: "Reports", icon: BarChart3, to: "/reports" },
        { label: "Audit", icon: ShieldCheck, to: "/audit" },
      ],
    },
    {
      items: [
        { label: "Notifications", icon: Bell, to: "/notifications", badge: unread || undefined },
        { label: "Billing", icon: CreditCard, to: slug ? `/o/${slug}/billing` : undefined },
        { label: "Settings", icon: SettingsIcon, to: "/settings" },
        { label: "Help Center", icon: HelpCircle, to: "/help" },
      ],
    },
  ];
}

interface SidebarNavItemProps {
  item: NavItem;
  pathname: string;
  onNavigate?: () => void;
}

function SidebarNavItem({ item, pathname, onNavigate }: SidebarNavItemProps) {
  const Icon = item.icon;
  const isActive = item.to && pathname === item.to;
  const content = (
    <span className={cn("relative flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors duration-200", isActive ? "text-dash-greenDeep" : "text-dash-muted", item.to && !isActive && "hover:bg-dash-hover/60 hover:text-dash-ink", !item.to && "cursor-default opacity-60")}>
      {isActive && <span className="absolute inset-0 rounded-xl bg-dash-hover animate-in fade-in duration-200" aria-hidden="true" />}
      <Icon className="relative z-10 size-4 shrink-0" />
      {/* `min-w-0` is what lets the truncate below actually bite — a flex child
          defaults to min-width:auto and would push a long street address
          straight through the sidebar's edge. */}
      <span className="relative z-10 min-w-0 flex-1">
        <span className="block truncate" title={item.hint ? item.label : undefined}>{item.label}</span>
        {item.hint && (
          <span className={cn("block truncate text-[11px] font-normal", isActive ? "text-dash-greenDeep/70" : "text-dash-faint")}>
            {item.hint}
          </span>
        )}
      </span>
      {typeof item.badge === "number" && (
        <Badge variant="outline" className={cn("relative z-10 flex h-[18px] min-w-[18px] shrink-0 items-center justify-center rounded-full border-transparent p-0 text-[10px] font-bold shadow-none", item.badge > 9 ? "px-1" : "w-[18px]", isActive ? "bg-dash-greenDeep text-white" : "bg-dash-chip text-dash-muted")}>
          {item.badge > 99 ? "99+" : item.badge}
        </Badge>
      )}
    </span>
  );

  const testId = item.testId || `dashboard-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`;
  return item.to
    ? <Link to={item.to} onClick={onNavigate} data-testid={testId}>{content}</Link>
    : <div data-testid={testId}>{content}</div>;
}

function PlanCard() {
  const { context } = useAuth();
  const { pathname } = useLocation();
  const slug = context?.organization?.slug;
  const usage = context?.billing?.usage as BillingUsage | undefined;
  if (!usage || !slug) return null;
  const billingPath = `/o/${slug}/billing`;
  // Every seat the plan includes — agent seats plus the assistant/admin seat —
  // so an Individual owner reads "1 / 2" (the assistant seat is still open)
  // rather than a full "1 / 1". A null agent limit is an uncapped plan.
  const seatsUsed = (usage.seats_used ?? 0) + (usage.support_seats_used ?? 0);
  const seatsLimit = usage.seats_limit == null ? null : usage.seats_limit + (usage.support_seats_limit ?? 0);
  const pct = seatsLimit ? Math.min(100, Math.round((seatsUsed / seatsLimit) * 100)) : 0;
  // `is_subscribed` reflects a real Stripe subscription (see deps.py's
  // compute_org_billing), not just a customer_id — a subscribed org has
  // nothing left to "upgrade" to from here; send it to manage the plan
  // it already has instead of repeating the trial-era upsell (BUG-007).
  const isSubscribed = Boolean(context?.billing?.is_subscribed);

  return (
    <div className="relative shrink-0 w-full overflow-hidden rounded-2xl bg-dash-sidebarDark p-5">
      <div className="pointer-events-none absolute -top-6 right-6 h-20 w-20 rounded-full bg-white/[0.06]" />
      <div className="pointer-events-none absolute top-16 left-8 h-28 w-28 rounded-full bg-white/[0.04]" />
      <div className="relative flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div className="flex size-8 items-center justify-center rounded-xl bg-white">
            <Home className="size-3.5 text-dash-greenDeep" />
          </div>
          <p className="font-dashboard text-[11px] font-semibold uppercase tracking-[0.15em] text-white/70">
            {usage.plan_name || "Plan"}
          </p>
        </div>
        <div>
          <p className="font-dashboard text-2xl font-bold text-white">
            {seatsUsed} <span className="text-base font-medium text-white/60">/ {seatsLimit ?? "∞"}</span>
          </p>
          <p className="font-dashboard text-xs text-white/90">
            seats used{typeof usage.deals_active === "number" ? ` · ${usage.deals_active} active deals` : ""}
          </p>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/50">
          <div className="h-full rounded-full bg-white" style={{ width: `${pct}%` }} />
        </div>
        {/* Hidden on the billing page itself, where a link to it would do nothing. */}
        {pathname !== billingPath && (
          <Link
            to={billingPath}
            className="flex w-full items-center justify-center rounded-xl border border-white/50 bg-dash-lime py-2 font-dashboard text-xs font-semibold text-white"
            data-testid="dashboard-sidebar-upgrade"
          >
            {isSubscribed ? "Manage plan" : "Upgrade plan ✦"}
          </Link>
        )}
      </div>
    </div>
  );
}

interface SidebarProps {
  onNavigate?: () => void;
  className?: string;
}

export default function Sidebar({ onNavigate, className }: SidebarProps) {
  const navGroups = useNavGroups();
  const { pathname } = useLocation();

  return (
    <aside
      className={cn(
        "flex h-full w-full max-w-[274px] shrink-0 flex-col items-stretch border border-dash-border bg-dash-surface font-dashboard",
        className
      )}
      data-testid="dashboard-sidebar"
    >
      <div className="flex items-center justify-center border-b border-dash-border px-4 py-[18px]">
        <img src="/logo.svg" alt="Stoop" className="h-10 w-auto" />
      </div>

      <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto px-3 py-3" data-testid="dashboard-sidebar-nav">
        {navGroups.map((group, groupIndex) => (
          <div key={group.label || group.items[0]?.label || groupIndex}>
            {groupIndex > 0 && <Separator className="my-2 bg-dash-chip" />}
            {group.label && (
              <p className="px-3 pb-1 pt-2 text-[10px] font-bold uppercase tracking-[0.18em] text-dash-faint">
                {group.label}
              </p>
            )}
            <div className="flex flex-col gap-0.5">
              {group.items.map((item) => (
                <SidebarNavItem key={item.to || item.label} item={item} pathname={pathname} onNavigate={onNavigate} />
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="px-3 pb-4">
        <PlanCard />
      </div>
    </aside>
  );
}
